import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    TablePagination,
    Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import React from 'react'
import { Logger } from 'wdc-cube'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { ExportDocumentRow, ExportDocumentsScope } from './export_documents_scope'

export type { ExportDocumentRow, ExportDocumentsResponse } from './export_documents_scope'

const LOG = Logger.get('ExportDocumentsDialog')

const rowsPerPageOptions = [10, 25, 50, 75, 100]

const DOC_TYPE_LABEL: Record<string, string> = {
    empresa: 'Empresa',
    socio: 'Sócio',
    medico: 'Médico',
    vinculo: 'Vínculo',
    acting_company: 'Empresa (Atuação)',
    acting_professional: 'Profissional (Atuação)'
}

export type ExportDocumentsDialogProps = {
    scope: ExportDocumentsScope
}

class ExportDocumentsDialogClass implements FCClassContext<ExportDocumentsDialogProps> {
    scope!: ExportDocumentsScope
    columns = static_build_columns()

    readonly emitClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitChangePage = (__e: unknown, newPage: number) => {
        this.scope.onChangePage(newPage).catch(LOG.caught)
    }

    readonly emitChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onChangeRowsPerPage(parseInt(e.target.value, 10)).catch(LOG.caught)
    }

    onSyncState({ scope }: ExportDocumentsDialogProps) {
        this.scope = scope
    }

    render({ scope }: ExportDocumentsDialogProps) {
        const data = scope.data

        return (
            <Dialog open={scope.open} onClose={this.emitClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Documentos da Exportação</span>
                    <IconButton onClick={this.emitClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {scope.loading && <LinearProgress color="secondary" />}
                    <Box sx={{ height: 480, width: '100%' }}>
                        <DataGrid
                            rows={data.records || []}
                            columns={this.columns}
                            getRowId={static_getRowId}
                            hideFooter
                            hideFooterPagination
                            disableColumnSelector
                        />
                    </Box>
                    {data.totalRecords > (data.limit || scope.limit) ? (
                        <TablePagination
                            component="div"
                            count={data.totalRecords}
                            page={scope.page}
                            onPageChange={this.emitChangePage}
                            rowsPerPage={scope.limit}
                            rowsPerPageOptions={rowsPerPageOptions}
                            onRowsPerPageChange={this.emitChangeRowsPerPage}
                        />
                    ) : (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary'
                            }}
                        >
                            {data.totalRecords} documento(s)
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        )
    }
}

export default classToFComponent(ExportDocumentsDialogClass, React)

function static_getRowId(row: ExportDocumentRow) {
    return `${row.seq}`
}

function static_build_columns(): GridColDef[] {
    return [
        {
            field: 'docLabel',
            headerName: 'Documento',
            flex: 1,
            minWidth: 240,
            valueGetter: (_value, row) => row.docLabel || row.docId
        },
        {
            field: 'docType',
            headerName: 'Tipo',
            width: 180,
            valueFormatter: (value) => DOC_TYPE_LABEL[value as string] || value
        },
        {
            field: 'docId',
            headerName: 'Identificador',
            width: 180
        },
        {
            field: 'userName',
            headerName: 'Exportado por',
            width: 180
        }
    ]
}
